/**
 * Settings v1.5 — shared vocabulary.
 *
 * The sibling of `v1_5/Header/theme.ts` and `v1_5/Chat/theme.ts`, and it follows the same contract:
 * the design system is light-only, so a dark-first component cannot read the global tokens
 * directly. Every settings part takes a `theme` prop, applies the class this file produces, and
 * reads only `--sv15-*` / `--hv15-*` custom properties. Light is a token swap, nothing else.
 */

import { cn } from '~/lib/utils';
import headerThemes from '../Header/HeaderTheme.module.css';
import settingsThemes from './SettingsTheme.module.css';

/**
 * NOT redeclared. A second `'dark' | 'light'` union is a second design system, so settings
 * re-exports the header's — the same thing `Layout/theme.ts` does one folder over.
 */
export type { HeaderTheme } from '../Header/theme';

/**
 * Applies BOTH theme layers.
 *
 * Settings reuses the header's `--hv15-*` ramp wholesale — text tones, fills, seams, the two
 * curves, every duration — rather than restating it under a second name, because a hairline that
 * differs between the application bar and the page under it is a bug, not a variant. `--sv15-*`
 * adds only the three planes the header never had to name (see `SettingsTheme.module.css`).
 *
 * Every settings component's root does
 * `className={cn(settingsThemeClass(theme), styles.root, className)}`. Custom properties inherit,
 * so a themed shell themes every card, field and button inside it for free.
 */
export function settingsThemeClass(theme: 'dark' | 'light' = 'dark'): string {
  return cn(headerThemes[theme], settingsThemes[theme]);
}

/**
 * The six destinations in the settings rail, in the order the rail draws them.
 *
 * Deliberately NOT `AccountAction` from the header's menu. That union carries `'sign-out'`, which
 * is an action rather than a destination — the rail draws it detached at the bottom and it can
 * never be the `active` page — and it spells the kits row `'brand-kits'` where this one says
 * `'kits'`. Two unions because they answer two different questions: "which row did you press" and
 * "which page are you on".
 */
export type SettingsNavItem = 'profile' | 'billing' | 'kits' | 'skills' | 'integrations' | 'settings';

/** Who is signed in, as the shell needs them: the rail's identity block and the bar's avatar. */
export interface SettingsUser {
  /** Full name. The rail prints it, and the avatar uses it as its accessible name. */
  name: string;
  /** Second line of the rail's identity block. Omit and the name sits alone, vertically centred. */
  company?: string;
  /**
   * Avatar fallback. Passed explicitly rather than derived from `name`, because the derivation
   * ("first letter of the first two words") gets a one-person team wrong — "Beka" is `B`, not `BT`
   * — and the account this renders is the one place that mistake is most visible.
   */
  initials: string;
  /** Profile picture. Falls back to `initials` when absent or when the image fails to load. */
  avatarUrl?: string;
}
